/**
 * Lógica pura del diálogo «Enviar» (docs/29): validar destinatarios, límites y adjunto, armar la
 * entrada de `POST /enviar` y llevar la lista de chats de WhatsApp por abrir. Sin pantalla: se
 * prueba en node.
 */
import {
  LIMITES_POR_DEFECTO,
  type AbrirWhatsApp,
  type AdjuntoBase64,
  type AppOrigen,
  type Destinatario,
  type EntradaEnviar,
  type LimitesDeEnvio,
  type ProveedorCorreo,
} from '../api/mensajes.js';

export type CanalDeEnvio = 'correo' | 'whatsapp';

export interface FilaDestinatario {
  nombre: string;
  /** Correo (canal correo) o teléfono con clave de país (canal WhatsApp). */
  contacto: string;
}

export interface FormularioEnvio {
  canal: CanalDeEnvio;
  proveedor: ProveedorCorreo | null;
  para: FilaDestinatario[];
  asunto: string;
  mensaje: string;
  enlace: { url: string; titulo: string } | null;
  /** Tamaño en bytes del adjunto (antes de base64) o null si no hay. */
  adjuntoBytes: number | null;
}

export interface ErroresEnvio {
  general: string[];
  /** Por índice de fila de `para`. */
  filas: Record<number, string>;
}

export const correoValido = (t: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t.trim());

/** Solo dígitos (con clave de país): 8 a 15. null si no es un teléfono. */
export function telefonoNormalizado(t: string): string | null {
  const d = t.replace(/\D/g, '');
  return d.length >= 8 && d.length <= 15 ? d : null;
}

export const MB = 1024 * 1024;

export function textoMB(bytes: number): string {
  return `${(bytes / MB).toLocaleString('es-MX', { maximumFractionDigits: 1 })} MB`;
}

/** Filas con algo escrito (las vacías se ignoran). */
export function filasUsadas(para: FilaDestinatario[]): FilaDestinatario[] {
  return para.filter((p) => p.nombre.trim() || p.contacto.trim());
}

export function validarAdjunto(bytes: number | null, limites: Pick<LimitesDeEnvio, 'adjunto_max_mb'>): string | null {
  if (bytes == null) return null;
  const max = (limites.adjunto_max_mb || LIMITES_POR_DEFECTO.adjunto_max_mb) * MB;
  return bytes > max
    ? `El archivo pesa ${textoMB(bytes)} y el máximo es ${limites.adjunto_max_mb || LIMITES_POR_DEFECTO.adjunto_max_mb} MB. Quítalo o manda un enlace.`
    : null;
}

export function validarEnvio(f: FormularioEnvio, limites: LimitesDeEnvio = LIMITES_POR_DEFECTO): ErroresEnvio {
  const general: string[] = [];
  const filas: Record<number, string> = {};
  const max = limites.destinatarios_max || LIMITES_POR_DEFECTO.destinatarios_max;

  const usadas = f.para
    .map((p, i) => ({ p, i }))
    .filter(({ p }) => p.nombre.trim() || p.contacto.trim());
  if (usadas.length === 0) general.push('Agrega al menos un destinatario.');
  if (usadas.length > max) general.push(`Puedes enviar a máximo ${max} destinatarios a la vez.`);

  for (const { p, i } of usadas) {
    const c = p.contacto.trim();
    if (f.canal === 'correo') {
      if (!c) filas[i] = 'Falta el correo.';
      else if (!correoValido(c)) filas[i] = 'Escribe un correo completo, como nombre@empresa.com.';
    } else if (!c) {
      filas[i] = 'Falta el teléfono.';
    } else if (!telefonoNormalizado(c)) {
      filas[i] = 'Escribe el número completo, con la clave de país (ej. 52 33 1234 5678).';
    }
  }

  if (f.canal === 'correo') {
    if (!f.proveedor) general.push('Conecta tu correo en Mensajes para poder enviar.');
    if (!f.asunto.trim()) general.push('Escribe el asunto.');
  }
  if (!f.mensaje.trim()) general.push('Escribe el mensaje.');

  const restantes = limites.por_dia - limites.usados_hoy;
  if (limites.por_dia > 0 && restantes <= 0) general.push('Llegaste al límite de envíos de hoy.');

  const adj = validarAdjunto(f.adjuntoBytes, limites);
  if (adj) general.push(adj);

  return { general, filas };
}

export const sinErrores = (e: ErroresEnvio): boolean => e.general.length === 0 && Object.keys(e.filas).length === 0;

export function armarEntrada(
  f: FormularioEnvio,
  origen: { app: AppOrigen; ref?: string },
  adjunto?: AdjuntoBase64 | null,
): EntradaEnviar {
  const para: Destinatario[] = filasUsadas(f.para).map((p) => {
    const nombre = p.nombre.trim() || undefined;
    return f.canal === 'correo'
      ? { ...(nombre ? { nombre } : {}), correo: p.contacto.trim() }
      : { ...(nombre ? { nombre } : {}), telefono: telefonoNormalizado(p.contacto) ?? p.contacto.trim() };
  });
  return {
    canal: f.canal,
    ...(f.canal === 'correo' && f.proveedor ? { proveedor: f.proveedor } : {}),
    para,
    ...(f.canal === 'correo' && f.asunto.trim() ? { asunto: f.asunto.trim() } : {}),
    mensaje: f.mensaje,
    // Si el mensaje ya trae el enlace escrito (ej. el texto de «Compartir con mi cliente»), no se
    // manda aparte: el hub lo agregaría otra vez al final.
    ...(f.enlace && !f.mensaje.includes(f.enlace.url) ? { enlace: f.enlace } : {}),
    ...(adjunto ? { adjunto } : {}),
    origen: { app: origen.app, ...(origen.ref ? { ref: origen.ref } : {}) },
  };
}

/** Al cambiar de canal: el contacto que no sirve para el nuevo (un correo en WhatsApp, o al revés) se borra. */
export function filasParaCanal(para: FilaDestinatario[], canal: CanalDeEnvio): FilaDestinatario[] {
  return para.map((p) => {
    const c = p.contacto.trim();
    const esCorreo = c.includes('@');
    const sirve = !c || (canal === 'correo' ? esCorreo : !esCorreo);
    return sirve ? p : { ...p, contacto: '' };
  });
}

/** Un chat por abrir tras un envío por WhatsApp (uno por clic, para no chocar con el bloqueo de ventanas). */
export interface ChatPorAbrir {
  etiqueta: string;
  url: string;
  abierto: boolean;
}

export function chatsPorAbrir(abrir: AbrirWhatsApp[], para: Destinatario[]): ChatPorAbrir[] {
  return abrir.map((a) => {
    const tel = a.telefono.replace(/\D/g, '');
    const quien = para.find((p) => p.telefono && p.telefono.replace(/\D/g, '') === tel);
    return { etiqueta: quien?.nombre?.trim() || a.telefono || 'tu contacto', url: a.url, abierto: false };
  });
}

export function marcarAbierto(lista: ChatPorAbrir[], i: number): ChatPorAbrir[] {
  return lista.map((c, j) => (j === i ? { ...c, abierto: true } : c));
}

export const pendientes = (lista: ChatPorAbrir[]): number => lista.filter((c) => !c.abierto).length;

/** Blob → base64 (sin el prefijo data:). */
export async function blobABase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  const PASO = 0x8000;
  for (let i = 0; i < bytes.length; i += PASO) {
    bin += String.fromCharCode(...bytes.subarray(i, i + PASO));
  }
  return btoa(bin);
}

/** Texto por omisión al enviar un presupuesto de OPUS ai / ci con su enlace. */
export function mensajeDelPresupuesto(nombre: string, url: string): string {
  return `Hola:\n\nTe comparto el presupuesto «${nombre}». Puedes verlo en este enlace:\n\n${url}\n\nSaludos.`;
}

/** Texto por omisión al enviar el PDF del resumen ejecutivo. */
export function mensajeDelResumen(obra: string | null | undefined): string {
  const de = obra?.trim() ? ` de ${obra.trim()}` : '';
  return `Hola:\n\nTe comparto el resumen ejecutivo${de} (va adjunto en PDF).\n\nSaludos.`;
}
